<template>
  <DashboardPanel title="收费进度与欠费账龄">
    <div class="collection">
      <div class="collection-rate">
        <div ref="gauge" class="collection-gauge"></div>
        <div class="collection-values">
          <p>本期目标<strong>{{ money(target) }} <small>元</small></strong></p>
          <p>已收金额<strong class="green">{{ money(received) }} <small>元</small></strong></p>
          <p>剩余金额<strong class="red">{{ money(remaining) }} <small>元</small></strong></p>
        </div>
      </div>
      <div class="arrears-age">
        <div class="arrears-age__title">欠费账龄分布（金额：元）</div>
        <div class="arrears-stack"><span v-for="item in ages" :key="item.label" :title="`${item.label}：${item.value} 元（${item.percent}）`" :style="{ width: item.percent, background: item.color }">{{ item.percent }}</span></div>
        <div class="arrears-legend"><div v-for="item in ages" :key="item.label" :title="`${item.label}：${item.value} 元（${item.percent}）`"><p><i :style="{ background: item.color }"></i>{{ item.label }}</p><strong>¥ {{ item.value }}</strong></div></div>
        <div class="arrears-total">欠费金额合计：<strong>{{ money(owing) }} 元</strong></div>
      </div>
    </div>
  </DashboardPanel>
</template>

<script>
import * as echarts from 'echarts'
import DashboardPanel from './DashboardPanel.vue'
import { addNumbers, dashboardTooltip, formatNumber, percentValue, roundNumber, subtractNumbers, toNumber } from '../dashboardFormat'
export default {
  name: 'CollectionProgress', components: { DashboardPanel },
  props: { data: { type: Object, default: () => ({}) } },
  data: () => ({ chart: null }),
  computed: {
    summary() { return this.data.summary || {} },
    target() { return toNumber(this.summary.receivableAmount) },
    received() { return toNumber(this.summary.receivedAmount) },
    owing() {
      return this.summary.oweAmount === undefined || this.summary.oweAmount === null
        ? Math.max(subtractNumbers(this.target, this.received), 0)
        : toNumber(this.summary.oweAmount)
    },
    remaining() { return Math.max(subtractNumbers(this.target, this.received), 0) },
    rate() { return Math.min(100, Math.max(0, percentValue(this.received, this.target))) },
    ages() {
      const rows = Array.isArray(this.data.aging) ? this.data.aging : []
      const labels = { '0-30': '30天内', '31-90': '31-90天', '91-180': '91-180天', '181+': '180天以上', unknown: '截止日缺失' }
      const colors = { '0-30': '#42b766', '31-90': '#2c83ec', '91-180': '#ef941c', '181+': '#ef4d46', unknown: '#71889a' }
      const total = addNumbers(rows.map(row => row.oweAmount)) || this.owing
      return rows.map(row => {
        const key = row.ageBucket || 'unknown'
        const value = toNumber(row.oweAmount)
        return { label: labels[key] || key, percent: `${formatNumber(percentValue(value, total), 1)}%`, value: this.money(value), color: colors[key] || '#71889a' }
      })
    }
  },
  watch: { data: { deep: true, handler() { this.renderChart() } } },
  mounted() { this.renderChart(); window.addEventListener('resize', this.resizeChart) },
  beforeDestroy() { window.removeEventListener('resize', this.resizeChart); if (this.chart) this.chart.dispose() },
  methods: {
    renderChart() {
      if (!this.chart) this.chart = echarts.init(this.$refs.gauge)
      const remainingRate = Math.max(subtractNumbers(100, this.rate, 1), 0)
      this.chart.setOption({
        tooltip: dashboardTooltip({
          trigger: 'item',
          formatter: params => `${params.marker}${params.name}：${formatNumber(params.value, 1)}%`
        }),
        series: [{
          type: 'pie',
          radius: ['68%', '88%'],
          center: ['50%', '50%'],
          startAngle: 90,
          label: { show: false },
          data: [
            { name: '已达成', value: roundNumber(this.rate, 1), itemStyle: { color: '#328af2' } },
            { name: '待完成', value: remainingRate, itemStyle: { color: '#3abb65' } }
          ]
        }],
        graphic: [{ type: 'text', left: 'center', top: '38%', style: { text: `${formatNumber(this.rate, 1)}%`, fill: '#edf6fd', fontSize: 20, fontWeight: 700, textAlign: 'center' } }, { type: 'text', left: 'center', top: '58%', style: { text: '已达成', fill: '#a9bccb', fontSize: 10, textAlign: 'center' } }]
      })
    },
    resizeChart() { if (this.chart) this.chart.resize() },
    money(value) { return formatNumber(value, 2) }
  }
}
</script>

<style lang="scss" scoped>
.collection { display: grid; grid-template-columns: 39% 61%; height: 100%; padding: 10px 13px 9px; }
.collection-rate { display: grid; grid-template-columns: 115px 1fr; align-items: center; border-right: 1px solid rgba(71, 125, 160, .34); }
.collection-gauge { width: 108px; height: 108px; }
.collection-values p { margin: 0 0 7px; color: #9fb1bf; font-size: 11px; }
.collection-values strong { display: block; margin-top: 2px; color: #eef6fb; font-size: 15px; }
.collection-values small { font-size: 9px; font-weight: 400; }.collection-values .green { color: #3bcb6e; }.collection-values .red { color: #f04b48; }
.arrears-age { padding-left: 18px; }
.arrears-age__title { height: 28px; color: #aabcca; font-size: 11px; }
.arrears-stack { display: flex; height: 28px; overflow: hidden; border-radius: 3px; }
.arrears-stack span { display: flex; align-items: center; justify-content: center; color: white; font-size: 11px; font-weight: 600; }
.arrears-legend { display: grid; grid-template-columns: repeat(4, 1fr); margin-top: 10px; }
.arrears-legend p { margin: 0; color: #a9bdcb; font-size: 10px; white-space: nowrap; }.arrears-legend i { display: inline-block; width: 9px; height: 9px; margin-right: 8px; border-radius: 2px; }.arrears-legend strong { display: block; margin: 5px 0 0 16px; color: #d5e2eb; font-size: 10px; font-weight: 400; white-space: nowrap; }
.arrears-total { margin-top: 12px; color: #a7bac8; font-size: 11px; text-align: center; }.arrears-total strong { color: #dfeaf2; font-size: 13px; }
</style>
